/**
 * Connected Apps tab.
 *
 * Pulls the catalog of available integrations from Composio live (via
 * `/api/toolkits`), renders each with its CoreUI brand icon, and merges
 * with per-org connection status from `/api/orgs/:orgId/connections`. Also
 * handles the `?status=success&connected=<toolkit>` query Composio appends
 * on OAuth callback.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useApi,
  type CatalogToolkit,
  type Connection,
  type ToolConfig,
} from '@/lib/api';
import { useToast } from '@/components/Toast';
import { BrandIcon } from '@/components/BrandIcon';
import {
  Link as LinkIcon,
  Check,
  Clock,
  AlertTriangle,
  Search,
  Loader2,
} from 'lucide-react';

type Status = 'connected' | 'pending' | 'failed' | 'disconnected';

function normalize(status: string): Status {
  const s = status.toLowerCase();
  if (s === 'active') return 'connected';
  if (s === 'initiated' || s === 'initializing' || s === 'pending') return 'pending';
  if (s === 'failed' || s === 'expired' || s === 'inactive') return 'failed';
  return 'disconnected';
}

export function ConnectedApps(_props: {
  orgId: string;
  tools: ToolConfig[];
  onChanged: () => void;
}) {
  const { orgId } = _props;
  const api = useApi();
  const toast = useToast();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [connections, setConnections] = useState<Map<string, Status>>(new Map());
  const [catalog, setCatalog] = useState<CatalogToolkit[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const toastedCallback = useRef(false);

  const reloadConnections = useCallback(async () => {
    try {
      const { connections: conns } = await api.get<{ connections: Connection[] }>(
        `/api/orgs/${orgId}/connections`,
      );
      const next = new Map<string, Status>();
      for (const c of conns) next.set(c.toolkit.toLowerCase(), normalize(c.status));
      setConnections(next);
    } catch (err) {
      toast.error('Could not load connections', (err as Error).message);
    }
  }, [api, orgId, toast]);

  const loadCatalog = useCallback(async () => {
    try {
      const r = await api.get<{
        toolkits: CatalogToolkit[];
        error?: string;
        detail?: string;
        kind?: string;
      }>('/api/toolkits');
      if (r.error === 'composio_not_configured') {
        setCatalogError('Composio is not configured — set COMPOSIO_API_KEY to load integrations.');
        setCatalog([]);
        return;
      }
      setCatalog(r.toolkits);
    } catch (err) {
      // The Worker returns 502 with { detail, kind } for catalog failures;
      // our fetch wrapper surfaces `err.data` so we can show the actual
      // Composio error instead of a generic "catalog_fetch_failed".
      const data = (err as { data?: { detail?: string; kind?: string } }).data;
      const detail = data?.detail ?? (err as Error).message;
      const kind = data?.kind ? `${data.kind}: ` : '';
      setCatalogError(`${kind}${detail}`);
      setCatalog([]);
    }
  }, [api]);

  useEffect(() => {
    void loadCatalog();
    void reloadConnections();
  }, [loadCatalog, reloadConnections]);

  // Composio appends `?status=success&connectedAccountId=…` to our callback
  // URL, and our worker adds `&connected=<toolkit>` for a human-readable
  // hint. Toast once on mount if we landed here from OAuth.
  useEffect(() => {
    if (toastedCallback.current) return;
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const status = params.get('status');
    if (!connected && !status) return;
    toastedCallback.current = true;

    if (status === 'success' || connected) {
      toast.success(
        'App connected',
        connected ? `${connected} is now authorized for your agent.` : undefined,
      );
    } else if (status === 'error' || status === 'failed') {
      toast.error('Connection failed', 'The OAuth flow did not complete — try again.');
    }
    // Clean the URL so a refresh doesn't re-fire the toast.
    const url = new URL(window.location.href);
    ['status', 'connected', 'connectedAccountId', 'connection_status'].forEach((k) =>
      url.searchParams.delete(k),
    );
    window.history.replaceState({}, '', url.toString());

    // Give Composio a beat to flip the account to ACTIVE before we re-list.
    const t = setTimeout(() => { void reloadConnections(); }, 1200);
    return () => clearTimeout(t);
  }, [reloadConnections, toast]);

  const connect = async (toolkit: string) => {
    setConnecting(toolkit);
    try {
      const { redirectUrl } = await api.post<{ redirectUrl: string }>(
        `/api/orgs/${orgId}/tools/composio/authorize`,
        { toolkit },
      );
      window.location.href = redirectUrl;
    } catch (err) {
      toast.error('Could not start OAuth', (err as Error).message);
      setConnecting(null);
    }
  };

  // Always float connected / pending integrations to the top so they
  // don't get lost when the catalog has hundreds of entries.
  const ordered = useMemo(() => {
    if (!catalog) return [];
    const rank = (slug: string) => {
      const s = connections.get(slug);
      if (s === 'connected') return 0;
      if (s === 'pending')   return 1;
      if (s === 'failed')    return 2;
      return 3;
    };
    return [...catalog].sort((a, b) => {
      const r = rank(a.slug) - rank(b.slug);
      if (r !== 0) return r;
      return a.name.localeCompare(b.name);
    });
  }, [catalog, connections]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? ordered.filter(
        t =>
          t.slug.toLowerCase().includes(q) ||
          t.name.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q),
      )
    : ordered;

  const DEFAULT_LIMIT = 24;
  const visible = showAll || q ? filtered : filtered.slice(0, DEFAULT_LIMIT);

  if (catalog === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading integrations from Composio…
      </div>
    );
  }

  if (catalogError && catalog.length === 0) {
    return (
      <div className="card p-4 text-sm text-muted-foreground max-w-xl">
        <div className="flex items-center gap-2 font-medium text-foreground">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          Integrations unavailable
        </div>
        <p className="mt-1">{catalogError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${ordered.length} integrations…`}
            className="input pl-8 w-full"
            aria-label="Search integrations"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {[...connections.values()].filter(s => s === 'connected').length} connected
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map((app) => {
          const status = connections.get(app.slug.toLowerCase()) ?? 'disconnected';
          return (
            <div
              key={app.slug}
              className="card p-4 flex items-start gap-3"
            >
              <BrandIcon slug={app.slug} fallbackLogo={app.logo} name={app.name} size={32} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate" title={app.name}>{app.name}</div>
                    {app.toolsCount > 0 && (
                      <div className="text-[11px] text-muted-foreground">
                        {app.toolsCount} tool{app.toolsCount === 1 ? '' : 's'}
                      </div>
                    )}
                  </div>
                  <StatusControl
                    status={status}
                    busy={connecting === app.slug}
                    onConnect={() => connect(app.slug)}
                  />
                </div>
                {app.description && (
                  <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">
                    {app.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!showAll && !q && filtered.length > DEFAULT_LIMIT && (
        <div className="flex justify-center">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowAll(true)}
          >
            Show all {filtered.length} integrations
          </button>
        </div>
      )}

      {q && filtered.length === 0 && (
        <div className="text-sm text-muted-foreground text-center py-8">
          No integrations match <span className="font-medium text-foreground">“{query}”</span>.
        </div>
      )}
    </div>
  );
}

function StatusControl({
  status,
  busy,
  onConnect,
}: {
  status: Status;
  busy: boolean;
  onConnect: () => void;
}) {
  if (status === 'connected') {
    return (
      <span
        className="badge bg-emerald-100 text-emerald-900 border-emerald-200 shrink-0"
        aria-label="Connected"
      >
        <Check className="h-3 w-3 mr-1" />
        Connected
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span
        className="badge bg-amber-100 text-amber-900 border-amber-200 shrink-0"
        aria-label="Authorization pending"
      >
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <button
        type="button"
        className="btn-secondary shrink-0"
        disabled={busy}
        onClick={onConnect}
        aria-label="Retry connection"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        {busy ? 'Opening…' : 'Retry'}
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn-primary shrink-0"
      disabled={busy}
      onClick={onConnect}
      aria-label="Connect app"
    >
      <LinkIcon className="h-3.5 w-3.5" />
      {busy ? 'Opening…' : 'Connect'}
    </button>
  );
}
